import { useEffect, useRef, useState } from 'react';

import clsx from 'clsx';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { TabPanel, TabView } from 'primereact/tabview';
import { useSearchParams } from 'react-router-dom';

import {
  useGetOpuEntriesPSEQuery,
  useGetStatsDataPSEQuery,
  useGetStatsDataQuery,
} from '@/app/services/aspirations';
import {
  useGetAvgDataQuery,
  useGetOpuEntriesQuery,
} from '@/app/services/aspirations';
import { Avgs, OpuEntries, OpuTechs } from '@/rtk-query.gql';

import { TransformedTechStats } from './types';

type AspirationStatsProps = {
  className?: string;
  aspirationDate: string;
  siteId: string;
  opuTechId?: string;
};

export const AspirationStats = ({
  className,
  aspirationDate,
  siteId,
  opuTechId,
}: AspirationStatsProps) => {
  const [params] = useSearchParams();
  const location = params.get('location');

  const [techId, setTechId] = useState<string>(opuTechId || '');
  const [pseId, setPseId] = useState<string>('');

  const [activeIndex, setActiveIndex] = useState(0);
  const [activeIndexPSE, setActiveIndexPSE] = useState(0);

  const opuEntriesRef = useRef<DataTable<OpuEntries[]>>(null);
  const opuEntriesRefPSE = useRef<DataTable<OpuEntries[]>>(null);
  const opuTechsRef = useRef<DataTable<TransformedTechStats[]>>(null);
  const opuTechsRefPSE = useRef<DataTable<TransformedTechStats[]>>(null);
  const teamStatsRef = useRef<DataTable<Avgs[]>>(null);

  const { data: statsData } = useGetStatsDataQuery({
    aspirationDate,
    siteId,
    location: location,
  });
  const techStats = statsData?.techStats as OpuTechs[];

  const { data: statsDataPSE, refetch: refetchStatsDataPSE } =
    useGetStatsDataPSEQuery({
      aspirationDate,
      siteId,
      location: location,
    });

  const techStatsPSE = statsDataPSE?.techStats as OpuTechs[];

  const { data: opuEntriesData } = useGetOpuEntriesQuery(
    { aspirationDate, siteId, techId },
    { skip: !techId, pollingInterval: 15000 },
  );

  const { data: opuEntriesDataPSE } = useGetOpuEntriesPSEQuery(
    { aspirationDate, siteId, pseId: pseId as string },
    { skip: !pseId, pollingInterval: 15000 },
  );

  const { data: teamStats } = useGetAvgDataQuery(
    { aspirationDate, siteId },
    { skip: !techId },
  );

  useEffect(() => {
    if (statsData && techStats && !techId) {
      setTechId(statsData?.techStats?.[0]?.techId as string);
    }
  }, [techId, statsData, statsData?.techStats, techStats, refetchStatsDataPSE]);

  useEffect(() => {
    if (statsDataPSE && techStatsPSE && !pseId) {
      setPseId(statsDataPSE?.techStats?.[0]?.techId as string);
    }
  }, [statsData, techStats, statsDataPSE, techStatsPSE, pseId]);

  const renderTechStatsTable = (techStats: OpuTechs) => {
    const transformedData = [
      {
        label: 'Total',
        follicles: techStats.totalFollicles,
        oocytes: techStats.totalgOocytes,
        numInMat: techStats.totalNumInMat,
      },
      {
        label: 'Average',
        follicles: techStats.avgFollicles,
        oocytes: techStats.avgOocytes,
        numInMat: techStats.avgNumInMat,
      },
    ] as TransformedTechStats[];

    return (
      <>
        <Button
          label="Export CSV"
          icon="pi pi-file-export"
          onClick={() => exportCSV('opuTechs')}
          severity="secondary"
        />
        <DataTable value={transformedData} scrollable ref={opuTechsRef}>
          <Column field="label" header="" />
          <Column field="follicles" header="Follicles" />
          <Column field="oocytes" header="Oocytes" />
          <Column field="numInMat" header="Num In Mat" />
        </DataTable>
      </>
    );
  };

  const renderTechStatsTablePSE = (techStats: OpuTechs) => {
    const transformedData = [
      {
        label: 'Total',
        follicles: techStats.totalFollicles,
        oocytes: techStats.totalgOocytes,
        numInMat: techStats.totalNumInMat,
      },
      {
        label: 'Average',
        follicles: techStats.avgFollicles,
        oocytes: techStats.avgOocytes,
        numInMat: techStats.avgNumInMat,
      },
    ] as TransformedTechStats[];
    return (
      <>
        <Button
          label="Export CSV"
          icon="pi pi-file-export"
          onClick={() => exportCSV('opuTechsPSE')}
          severity="secondary"
        />
        <DataTable value={transformedData} scrollable ref={opuTechsRefPSE}>
          <Column field="label" header="" />
          <Column field="follicles" header="Follicles" />
          <Column field="oocytes" header="Oocytes" />
          <Column field="numInMat" header="Num In Mat" />
        </DataTable>
      </>
    );
  };

  const exportCSV = (
    type:
      | 'opuTechs'
      | 'opuEntries'
      | 'opuEntriesRefPSE'
      | 'teamStats'
      | 'opuTechsPSE',
  ) => {
    const dt =
      type === 'opuTechs'
        ? opuTechsRef
        : type === 'opuEntries'
          ? opuEntriesRef
          : type === 'opuTechsPSE'
            ? opuTechsRefPSE
            : type === 'opuEntriesRefPSE'
              ? opuEntriesRefPSE
              : teamStatsRef;
    dt.current?.exportCSV();
  };

  const handleTabChange = (e: { index: number }) => {
    setActiveIndex(e.index);
    if (techStats[e.index]) {
      setTechId(techStats?.[e.index]?.techId as string);
    }
  };

  const handleTabChangePSE = (e: { index: number }) => {
    setActiveIndexPSE(e.index);
    if (techStatsPSE[e.index]) {
      setPseId(techStatsPSE?.[e.index]?.techId as string);
    }
  };

  const mainTabViewClassName = clsx('main-tabview', 'tab-view-shading');
  const innerTabViewClassName = clsx(
    `aspiration-stats${className ? ` ${className}` : ''} dialog-width`,
    'tab-view-shading',
  );

  return (
    <>
      <TabView className={mainTabViewClassName}>
        <TabPanel header="Techs">
          {techStats?.length > 0 ? (
            <TabView
              className={innerTabViewClassName}
              activeIndex={activeIndex}
              onTabChange={handleTabChange}
            >
              {techStats?.map((techStat: OpuTechs) => (
                <TabPanel key={techStat.techId} header={techStat.tech}>
                  <h3>OPU Entries</h3>
                  <Button
                    label="Export CSV"
                    icon="pi pi-file-export"
                    onClick={() => exportCSV('opuEntries')}
                    severity="secondary"
                  />
                  <DataTable
                    value={opuEntriesData}
                    scrollable
                    ref={opuEntriesRef}
                  >
                    <Column field="donorName" header="Donor Name" />
                    <Column field="follicles" header="Follicles" />
                    <Column field="oocytes" header="Oocytes" />
                    <Column field="numInMat" header="Num In Mat" />
                    <Column field="ivmCyto" header="IVM Cyto" />
                    <Column field="comment" header="Comment" />
                    <Column field="aspTime" header="Asp Time" />
                    <Column field="rowNo" header="Row No" />
                  </DataTable>
                  <h3>Tech Average Data</h3>
                  {renderTechStatsTable(techStat)}
                  <h3>Team Stats</h3>
                  <Button
                    label="Export CSV"
                    icon="pi pi-file-export"
                    onClick={() => exportCSV('teamStats')}
                    severity="secondary"
                  />
                  <DataTable value={teamStats} scrollable ref={teamStatsRef}>
                    <Column
                      field="label"
                      body={(rowData) =>
                        rowData.blank === 'Total' ? 'Total' : 'Average'
                      }
                    />
                    <Column field="follicles" header="Follicles" />
                    <Column field="oocytes" header="Oocytes" />
                    <Column field="numInMat" header="Num In Mat" />
                  </DataTable>
                </TabPanel>
              ))}
            </TabView>
          ) : (
            <div>No Stats Available</div>
          )}
        </TabPanel>

        <TabPanel header="PSEs">
          {techStatsPSE?.length > 0 ? (
            <TabView
              className={innerTabViewClassName}
              activeIndex={activeIndexPSE}
              onTabChange={handleTabChangePSE}
            >
              {techStatsPSE?.map((techStat: OpuTechs) => (
                <TabPanel
                  key={techStat.techId}
                  header={techStat.tech + ' (PSE)'}
                >
                  <h3>OPU Entries</h3>
                  <Button
                    label="Export CSV"
                    icon="pi pi-file-export"
                    onClick={() => exportCSV('opuEntriesRefPSE')}
                    severity="secondary"
                  />
                  <DataTable
                    value={opuEntriesDataPSE}
                    scrollable
                    ref={opuEntriesRefPSE}
                  >
                    <Column field="donorName" header="Donor Name" />
                    <Column field="follicles" header="Follicles" />
                    <Column field="oocytes" header="Oocytes" />
                    <Column field="numInMat" header="Num In Mat" />
                    <Column field="ivmCyto" header="IVM Cyto" />
                    <Column field="comment" header="Comment" />
                    <Column field="aspTime" header="Asp Time" />
                    <Column field="rowNo" header="Row No" />
                  </DataTable>
                  <h3>Tech Average Data</h3>
                  {renderTechStatsTablePSE(techStat)}
                  <h3>Team Stats</h3>
                  <Button
                    label="Export CSV"
                    icon="pi pi-file-export"
                    onClick={() => exportCSV('teamStats')}
                    severity="secondary"
                  />
                  <DataTable value={teamStats} scrollable ref={teamStatsRef}>
                    <Column
                      field="label"
                      body={(rowData) =>
                        rowData.blank === 'Total' ? 'Total' : 'Average'
                      }
                    />
                    <Column field="follicles" header="Follicles" />
                    <Column field="oocytes" header="Oocytes" />
                    <Column field="numInMat" header="Num In Mat" />
                  </DataTable>
                </TabPanel>
              ))}
            </TabView>
          ) : (
            <div>No Stats Available</div>
          )}
        </TabPanel>
      </TabView>
    </>
  );
};
